import React, { useState, useEffect, useCallback, useRef } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { toast } from "react-toastify";
import { PulseLoader } from "react-spinners";

const API_URL = process.env.REACT_APP_API_URL;

const DEFAULT_SORT_BY = "poDate";
const DEFAULT_SORT_ORDER = "desc";
const FILTER_KEY = "poSupplierListFilters";

const SORT_COLUMN_MAP = {
  poNumber: "poNumber",
  poDate: "poDate",
  poDeliveryDate: "poDeliveryDate",
};

const SUPPLIER_PO_STAT_CARDS = [
  {
    key: "totalCount",
    label: "Total PO",
    titleColor: "#1F4E79",
    valueColor: "#2874A6",
    filterValue: null,
  },
];

const SupplierPoStatCardSkeleton = () => (
  <div className="procurement-stat-col">
    <div className="card supplier-stat-card">
      <div className="card-body">
        <div className="stat-skeleton stat-skeleton-title" aria-hidden="true" />
        <div className="stat-skeleton stat-skeleton-value" aria-hidden="true" />
      </div>
    </div>
  </div>
);

const SupplierPoStatCard = ({
  label,
  value,
  titleColor,
  valueColor,
  onClick,
  isActive,
}) => (
  <div className="procurement-stat-col">
    <div
      className={`card supplier-stat-card procurement-stat-card-clickable${
        isActive ? " procurement-stat-card-active" : ""
      }`}
      onClick={onClick}
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          onClick();
        }
      }}
      role="button"
      tabIndex={0}
      title={`Filter by ${label}`}
    >
      <div className="card-body">
        <div className="stat-title" style={{ color: titleColor }}>
          {label}
        </div>
        <div className="stat-value" style={{ color: valueColor }}>
          {value}
        </div>
      </div>
    </div>
  </div>
);


function parseJsonIfString(value) {
  if (typeof value !== "string") return value;
  try {
    return JSON.parse(value);
  } catch {
    return value;
  }
}

function parseApiResponse(raw) {
  let d = parseJsonIfString(raw);
  if (d?.body != null) return parseJsonIfString(d.body);
  return d;
}

function isApiSuccess(res) {
  if (!res || typeof res !== "object") return false;
  const s = res.success;
  return s === true || s === "true" || s === 1 || s === "1";
}

function extractPoPayload(res) {
  let block = parseJsonIfString(res?.data != null ? res.data : res);
  if (!block || typeof block !== "object") return { pos: [], total: 0 };
  const list = parseJsonIfString(block.pos || block.items || []);
  const pos = Array.isArray(list) ? list : [];
  const total =
    typeof block.totalCount === "number" ? block.totalCount : pos.length;
  return { pos, total };
}

function formatPoDate(value) {
  if (!value) return "-";
  const str = String(value).trim();
  if (/^\d{2}-\d{2}-\d{4}$/.test(str)) return str;
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return str || "-";
  return d.toLocaleDateString("en-GB", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    timeZone: "Asia/Kolkata",
  });
}

function getPoId(po) {
  return po?._id || po?.id || po?.poId || "";
}

function renderPoStatus(status) {
  const value = String(status || "").trim();
  if (!value) return "-";
  const n = value.toLowerCase();
  let badge = "badge-info";
  if (n === "draft") badge = "badge-secondary";
  if (n.includes("waiting")) badge = "badge-warning";
  if (n.includes("approved")) badge = "badge-success";
  return <span className={`badge ${badge}`}>{value}</span>;
}

const PoSupplierList = () => {
  const savedFilters = JSON.parse(sessionStorage.getItem(FILTER_KEY) || "{}");

  const [pos, setPos] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isPoCountLoading, setIsPoCountLoading] = useState(true);
  const [poCount, setPoCount] = useState({
    totalCount: 0,
  });
  const [searchQuery, setSearchQuery] = useState(savedFilters.searchQuery || "");
  const [activeSearchQuery, setActiveSearchQuery] = useState(
    savedFilters.activeSearchQuery || "",
  );
  const [totalCount, setTotalCount] = useState(0);
  const [currentPage, setCurrentPage] = useState(savedFilters.currentPage || 1);
  const [itemsPerPage, setItemsPerPage] = useState(
    savedFilters.itemsPerPage || 25,
  );
  const [poStatus, setPoStatus] = useState(savedFilters.poStatus || "");
  const [sortBy, setSortBy] = useState(savedFilters.sortBy || DEFAULT_SORT_BY);
  const [sortOrder, setSortOrder] = useState(
    savedFilters.sortOrder || DEFAULT_SORT_ORDER,
  );
  const loadRequestIdRef = useRef(0);

  const hasActiveFilters = Boolean(activeSearchQuery || poStatus);

  useEffect(() => {
    sessionStorage.setItem(
      FILTER_KEY,
      JSON.stringify({
        searchQuery,
        activeSearchQuery,
        currentPage,
        itemsPerPage,
        poStatus,
        sortBy,
        sortOrder,
      }),
    );
  }, [
    searchQuery,
    activeSearchQuery,
    currentPage,
    itemsPerPage,
    poStatus,
    sortBy,
    sortOrder,
  ]);

  const fetchPoCount = useCallback(
    async (requestId) => {
      setIsPoCountLoading(true);
      try {
        const payload = {
          action: "get-supplier-po-count",
          currentUserId: localStorage.getItem("id") || undefined,
        };
        const q = activeSearchQuery.trim();
        if (q) payload.search = q;

        const response = await axios.post(API_URL, payload);
        if (requestId !== loadRequestIdRef.current) return;

        const res = parseApiResponse(response.data);
        if (isApiSuccess(res)) {
          const data = parseJsonIfString(res.data);
          const d = data && typeof data === "object" ? data : {};
          setPoCount({
            totalCount: Number(d.totalCount) || 0,
          });
        }
      } catch (error) {
        if (requestId !== loadRequestIdRef.current) return;
        console.error("Supplier PO Stats Error:", error);
      } finally {
        if (requestId === loadRequestIdRef.current) setIsPoCountLoading(false);
      }
    },
    [activeSearchQuery],
  );

  const fetchPos = useCallback(
    async (requestId) => {
      setIsLoading(true);
      try {
        const payload = {
          action: "get-supplier-po",
          currentUserId: localStorage.getItem("id") || undefined,
          page: currentPage,
          limit: itemsPerPage,
          sortBy,
          sortOrder,
        };
        const q = activeSearchQuery.trim();
        if (q) payload.search = q;
        if (poStatus) payload.poStatus = poStatus;

        const response = await axios.post(API_URL, payload);
        if (requestId !== loadRequestIdRef.current) return;

        const res = parseApiResponse(response.data);
        if (isApiSuccess(res)) {
          const { pos: list, total } = extractPoPayload(res);
          setPos(list);
          setTotalCount(total);
        } else {
          toast.error(res?.message || "Failed to fetch supplier POs", {
            autoClose: 2000,
          });
          setPos([]);
          setTotalCount(0);
        }
      } catch (error) {
        if (requestId !== loadRequestIdRef.current) return;
        console.error("Fetch supplier POs error:", error);
        const errData = error.response?.data
          ? parseApiResponse(error.response.data)
          : null;
        toast.error(
          errData?.message || "An error occurred while fetching supplier POs",
          { autoClose: 2000 },
        );
        setPos([]);
        setTotalCount(0);
      } finally {
        if (requestId === loadRequestIdRef.current) setIsLoading(false);
      }
    },
    [currentPage, itemsPerPage, activeSearchQuery, poStatus, sortBy, sortOrder],
  );

  useEffect(() => {
    const requestId = ++loadRequestIdRef.current;
    let cancelled = false;
    const load = async () => {
      await fetchPoCount(requestId);
      if (cancelled || requestId !== loadRequestIdRef.current) return;
      await fetchPos(requestId);
    };
    load();
    return () => {
      cancelled = true;
    };
  }, [fetchPos, fetchPoCount]);

  const totalPages = Math.max(1, Math.ceil(totalCount / itemsPerPage) || 1);
  const startItem = pos.length > 0 ? (currentPage - 1) * itemsPerPage + 1 : 0;
  const endItem =
    pos.length > 0 ? Math.min(currentPage * itemsPerPage, totalCount) : 0;

    const handleSearchChange = (e) => {
    const value = e.target.value;
    setSearchQuery(value);
    if (value.trim() === "" && activeSearchQuery) {
      setActiveSearchQuery("");
      setCurrentPage(1);
    }
  };

  const handleSearchTrigger = () => {
    setActiveSearchQuery(searchQuery.trim());
    setCurrentPage(1);
  };

  const handleSearchKeyPress = (e) => {
    if (e.key === "Enter") {
      e.preventDefault();
      handleSearchTrigger();
    }
  };

  const isStatCardActive = (card) =>
    !card.filterValue ? !poStatus : poStatus === card.filterValue;

  const handleStatCardClick = (card) => {
    setPoStatus(card.filterValue || "");
    if (currentPage !== 1) setCurrentPage(1);
  };

  const handleClearFilter = () => {
    setPoStatus("");
    if (currentPage !== 1) setCurrentPage(1);
  };

  const handleClearAll = () => {
    setSearchQuery("");
    setActiveSearchQuery("");
    handleClearFilter();
  };

  const handleItemsPerPageChange = (e) => {
    setItemsPerPage(Number(e.target.value) || 25);
    setCurrentPage(1);
  };

  const handlePageChange = (page) => {
    if (page >= 1 && page <= totalPages) {
      setCurrentPage(page);
      setTimeout(() => window.scrollTo({ top: 0, behavior: "smooth" }), 100);
    }
  };

  const handleSort = (column) => {
    const apiField = SORT_COLUMN_MAP[column] || column;
    if (sortBy === apiField) {
      setSortOrder((prev) => (prev === "asc" ? "desc" : "asc"));
    } else {
      setSortBy(apiField);
      setSortOrder("asc");
    }
    setCurrentPage(1);
  };

  const getSortIcon = (column) => {
    const apiField = SORT_COLUMN_MAP[column] || column;
    if (sortBy !== apiField) {
      return (
        <i
          className="fa-solid fa-sort ml-1 text-muted"
          style={{ fontSize: "0.8em", flexShrink: 0 }}
        />
      );
    }
    return (
      <i
        className={`fa-solid ${
          sortOrder === "asc" ? "fa-sort-up" : "fa-sort-down"
        } ml-1`}
        style={{ fontSize: "0.8em", flexShrink: 0 }}
      />
    );
  };

  const renderSortableHeader = (column, label, extraStyle = {}) => (
    <th
      onClick={() => handleSort(column)}
      style={{ cursor: "pointer", whiteSpace: "nowrap", ...extraStyle }}
    >
      <span style={{ display: "inline-flex", alignItems: "center" }}>
        {label}
        {getSortIcon(column)}
      </span>
    </th>
  );

  const getPageNumbers = () => {
    const pages = [];
    const max = 7;
    const tp = totalPages;
    if (tp <= max) {
      for (let i = 1; i <= tp; i++) pages.push(i);
    } else if (currentPage <= 4) {
      for (let i = 1; i <= 5; i++) pages.push(i);
      pages.push("ellipsis", tp);
    } else if (currentPage >= tp - 3) {
      pages.push(1, "ellipsis");
      for (let i = tp - 4; i <= tp; i++) pages.push(i);
    } else {
      pages.push(1, "ellipsis");
      for (let i = currentPage - 1; i <= currentPage + 1; i++) pages.push(i);
      pages.push("ellipsis", tp);
    }
    return pages;
  };

  return (
    <div>
      <div className="content-header">
        <div className="container-fluid">
          <div className="row mb-2 align-items-center">
            <div className="col-sm-6">
              <h1 className="m-0 txt_lft">Purchase Orders</h1>
            </div>
          </div>
        </div>
      </div>

      <div className="content">
        <div className="container-fluid">
          <div className="row">
            <div className="col-lg-12">
              <div className="procurement-stat-card-row rfq-stat-card-row">
                {isPoCountLoading
                  ? SUPPLIER_PO_STAT_CARDS.map((card) => (
                      <SupplierPoStatCardSkeleton key={card.key} />
                    ))
                  : SUPPLIER_PO_STAT_CARDS.map((card) => (
                      <SupplierPoStatCard
                        key={card.key}
                        label={card.label}
                        value={poCount[card.key]}
                        titleColor={card.titleColor}
                        valueColor={card.valueColor}
                        isActive={isStatCardActive(card)}
                        onClick={() => handleStatCardClick(card)}
                      />
                    ))}
              </div>

              <div>
                <div className="card-body">
                  <div className="row align-items-end mb-4 flex-xl-nowrap">
                    <div className="col-12 col-sm-6 col-md-4 col-xl-3 mb-2 mb-xl-0">
                      <div className="form-group mb-0">
                        <label
                          className="mb-1"
                          style={{ fontSize: "13px", fontWeight: "500" }}
                        >
                          Search
                        </label>
                        <div className="input-group">
                          <input
                            type="text"
                            className="form-control"
                            placeholder="Search POs..."
                            value={searchQuery}
                            onChange={handleSearchChange}
                            onKeyPress={handleSearchKeyPress}
                          />
                          <div className="input-group-append">
                            <div
                              className="input-group-text crsr_pntr"
                              onClick={handleSearchTrigger}
                              style={{ cursor: "pointer" }}
                            >
                              <i className="fa-solid fa-search" />
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>

                    {Boolean(poStatus) && (
                      <div className="col-12 col-sm-6 col-md-4 col-xl-auto mb-2 mb-xl-0">
                        <button
                          type="button"
                          className="btn btn-secondary btn-sm w-100"
                          onClick={handleClearFilter}
                          style={{ minWidth: "100px", whiteSpace: "nowrap" }}
                        >
                          <i className="fa-solid fa-times mr-1" />
                          Clear Filter
                        </button>
                      </div>
                    )}
                  </div>
                </div>
              </div>

              {isLoading && pos.length === 0 ? (
                <div className="text-center p-4">
                  <PulseLoader color="#1199B2" size={10} />
                </div>
              ) : pos.length === 0 && !hasActiveFilters ? (
                <div className="nocontent_div text-center p-4">
                  There are no POs yet.
                </div>
              ) : pos.length === 0 && hasActiveFilters ? (
                <div className="nocontent_div text-center p-4">
                  No POs found matching your filters.
                  <br />
                  <div className="mt-3">
                    <button
                      type="button"
                      className="btn btn-blue"
                      onClick={handleClearAll}
                    >
                      Clear Filters
                    </button>
                  </div>
                </div>
              ) : (
                <div
                  className="supply-table-container"
                  style={{ position: "relative" }}
                >
                  {isLoading && (
                    <div
                      style={{
                        position: "absolute",
                        inset: 0,
                        zIndex: 2,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        backgroundColor: "rgba(255, 255, 255, 0.55)",
                        pointerEvents: "none",
                      }}
                    >
                      <PulseLoader color="#1199B2" size={10} />
                    </div>
                  )}
                  <div
                    className="table-responsive"
                    style={{
                      opacity: isLoading ? 0.6 : 1,
                      transition: "opacity 0.15s ease",
                      pointerEvents: isLoading ? "none" : "auto",
                    }}
                  >
                    <table
                      id="supplierPos"
                      width="100%"
                      className="table table-striped table-bordered txt_lft"
                    >
                      <thead>
                        <tr>
                          <th
                            style={{
                              whiteSpace: "nowrap",
                              width: "40px",
                              minWidth: "40px",
                            }}
                          >
                            S.No
                          </th>
                          {renderSortableHeader("poNumber", "PO Number", {
                            minWidth: "120px",
                          })}
                          {renderSortableHeader("poDate", "PO Date", {
                            minWidth: "100px",
                          })}
                          {renderSortableHeader(
                            "poDeliveryDate",
                            "Delivery Date",
                            { minWidth: "120px" },
                          )}
                          <th style={{ minWidth: "180px" }}>PO Summary</th>
                          <th style={{ minWidth: "120px" }}>PO Status</th>
                          <th style={{ width: "80px", minWidth: "80px" }}>
                            Action
                          </th>
                        </tr>
                      </thead>
                      <tbody>
                        {pos.map((po, index) => {
                          const id = getPoId(po);
                          return (
                            <tr key={id || index}>
                              <td>
                                {(currentPage - 1) * itemsPerPage + index + 1}
                              </td>
                              <td style={{ whiteSpace: "nowrap" }}>
                                {po?.poNumber || "-"}
                              </td>
                              <td style={{ whiteSpace: "nowrap" }}>
                                {formatPoDate(po?.poDate)}
                              </td>
                              <td style={{ whiteSpace: "nowrap" }}>
                                {formatPoDate(po?.poDeliveryDate)}
                              </td>
                              <td
                                style={{
                                  maxWidth: "240px",
                                  wordWrap: "break-word",
                                  whiteSpace: "normal",
                                }}
                              >
                                {String(po?.poSummary || "").trim() || "-"}
                              </td>
                              <td style={{ whiteSpace: "nowrap" }}>
                                {renderPoStatus(po?.poStatus)}
                              </td>
                              <td>
                                <div
                                  align="center"
                                  style={{ whiteSpace: "nowrap" }}
                                  className="d-flex justify-content-center align-items-center"
                                >
                                  <Link
                                    to={`/poSupplierView/${id}`}
                                    className="crsr_pntr"
                                    title="View"
                                  >
                                    <i className="fa-solid fa-eye" />
                                  </Link>
                                </div>
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>

                  <div
                    className="supply-pagination-container"
                    style={{
                      opacity: isLoading ? 0.65 : 1,
                      pointerEvents: isLoading ? "none" : "auto",
                    }}
                  >
                    <div className="pagination-wrapper">
                      <div className="pagination-info">
                        <label
                          className="mb-0 mr-2"
                          style={{ fontSize: "13px" }}
                        >
                          Show:
                        </label>
                        <select
                          className="form-control form-control-sm"
                          value={itemsPerPage}
                          onChange={handleItemsPerPageChange}
                        >
                          <option value="25">25</option>
                          <option value="50">50</option>
                          <option value="100">100</option>
                        </select>
                        <span className="ml-2" style={{ fontSize: "13px" }}>
                          entries
                        </span>
                        <span
                          className="ml-3 text-muted"
                          style={{ fontSize: "13px" }}
                        >
                          {startItem}–{endItem} of {totalCount}
                        </span>
                      </div>

                      <nav className="pagination-nav">
                        <ul className="pagination mb-0">
                          <li
                            className={`page-item ${
                              currentPage === 1 ? "disabled" : ""
                            }`}
                          >
                            <button
                              className="page-link"
                              onClick={() => handlePageChange(1)}
                              disabled={currentPage === 1}
                              title="First Page"
                            >
                              <i className="fa-solid fa-angle-double-left" />
                            </button>
                          </li>
                          <li
                            className={`page-item ${
                              currentPage === 1 ? "disabled" : ""
                            }`}
                          >
                            <button
                              className="page-link"
                              onClick={() => handlePageChange(currentPage - 1)}
                              disabled={currentPage === 1}
                              title="Previous Page"
                            >
                              <i className="fa-solid fa-angle-left" />
                            </button>
                          </li>
                          {getPageNumbers().map((page, index) =>
                            page === "ellipsis" ? (
                              <li
                                key={`ellipsis-${index}`}
                                className="page-item disabled"
                              >
                                <span className="page-link">...</span>
                              </li>
                            ) : (
                              <li
                                key={page}
                                className={`page-item ${
                                  currentPage === page ? "active" : ""
                                }`}
                              >
                                <button
                                  className="page-link"
                                  onClick={() => handlePageChange(page)}
                                >
                                  {page}
                                </button>
                              </li>
                            ),
                          )}
                          <li
                            className={`page-item ${
                              currentPage === totalPages ? "disabled" : ""
                            }`}
                          >
                            <button
                              className="page-link"
                              onClick={() => handlePageChange(currentPage + 1)}
                              disabled={currentPage === totalPages}
                              title="Next Page"
                            >
                              <i className="fa-solid fa-angle-right" />
                            </button>
                          </li>
                          <li
                            className={`page-item ${
                              currentPage === totalPages ? "disabled" : ""
                            }`}
                          >
                            <button
                              className="page-link"
                              onClick={() => handlePageChange(totalPages)}
                              disabled={currentPage === totalPages}
                              title="Last Page"
                            >
                              <i className="fa-solid fa-angle-double-right" />
                            </button>
                          </li>
                        </ul>
                      </nav>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PoSupplierList;